import React, { useEffect, useState, memo } from 'react';

import { DataState } from '../../context/DataProvider'; // Assuming DataState provides userGoogleId
import { getOrFetchSignedUrl } from './imageCache'; // Adjust path if necessary

const AuthImage = ({ src, alt, className }) => {
  const [signedUrl, setSignedUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true); // Manage loading state explicitly
  const [error, setError] = useState(null); // Store error object or message

  const { userGoogleId: token } = DataState();

  // Determine if this URL needs protected fetch
  // Ensure src exists before calling .includes
  const isProtected = src && src.includes('/file/s3/task/');

  useEffect(() => {
    // Reset states when src or protection status changes to avoid showing stale data
    setSignedUrl(null);
    setError(null);
    // Set loading to true only if we are actually going to fetch
    setIsLoading(isProtected && !!src && !!token);

    if (!isProtected) {
      // For non-protected images, src is the final URL.
      // No fetch needed, and not loading.
      setIsLoading(false);
      return;
    }

    // From here, isProtected is true.

    if (!src) {
      setError(new Error('Image source is missing.'));
      setIsLoading(false);
      return;
    }

    if (!token) {
      setError(new Error('Authentication token is missing.'));
      setIsLoading(false);
      return;
    }

    let canceled = false;

    // At this point, isLoading was set to true.
    getOrFetchSignedUrl(src, token)
      .then((url) => {
        if (!canceled) {
          setSignedUrl(url);
          setError(null); // Clear any previous error
        }
      })
      .catch((err) => {
        if (!canceled) {
          setError(err); // Set the error state
          setSignedUrl(null); // Ensure no old signedUrl is shown
        }
      })
      .finally(() => {
        if (!canceled) {
          setIsLoading(false); // Loading is complete, whether success or failure
        }
      });

    return () => {
      canceled = true;
    };
  }, [src, token, isProtected]); // Effect dependencies

  // --- Render Logic ---

  // Case 1: Non-protected image
  if (!isProtected) {
    if (!src) {
      // Handle missing src for public images, though ideally src should always be provided
      return (
        <div className={className || ''}>Image source missing. {alt || ''}</div>
      );
    }
    return <img src={src} alt={alt || ''} className={className || ''} />;
  }

  // Case 2: Protected image, still loading
  if (isLoading) {
    return <div className={className || ''}>Loading: {alt || 'image'}…</div>;
  }

  // Case 3: Protected image, error occurred
  if (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'An unknown error occurred.';
    return (
      <div className={className || ''}>
        Failed to load: {alt || 'image'}. <small>({errorMessage})</small>
      </div>
    );
  }

  // Case 4: Protected image, successfully loaded signed URL
  if (signedUrl) {
    return (
      <img
        src={signedUrl}
        alt={alt || ''}
        className={className || ''}
        onError={(e) => {
          // This onError on the <img> tag handles issues with the signedUrl itself
          // (e.g., URL expired, network error loading from S3, malformed image data).
          setError(
            new Error('Image could not be displayed from the provided URL.'),
          );
          setSignedUrl(null); // Clear the problematic URL
          // Optionally, you could try to clear this specific src from the cache if it's consistently failing
          // import { clearImageCacheForSrc } from './imageCache';
          // clearImageCacheForSrc(src);
        }}
      />
    );
  }

  // Fallback (e.g., if src for a protected image was missing initially but didn't set error,
  // or some unexpected state). Should be less common with current logic.
  if (!src && isProtected) {
    return (
      <div className={className || ''}>
        Image source missing for protected image. {alt || ''}
      </div>
    );
  }

  return <div className={className || ''}>Preparing: {alt || 'image'}…</div>;
};

export default memo(AuthImage);
